import React, { useState, useRef, useCallback } from 'react'
import { Upload, FileText, CheckCircle2, AlertCircle, Loader2, RefreshCw } from 'lucide-react'
import { useApplicationFormStore } from '../../stores/application-form-store'
import { useGenerateCvUploadUrl } from '../../hooks/useGenerateCvUploadUrl'
import { useTranslation } from '../../hooks/useTranslation'

const ACCEPTED_FILE_TYPES = ['.pdf', '.doc', '.docx']
const MAX_FILE_SIZE = 10485760 // 10MB in bytes
const ACCEPTED_MIME_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
]

const MIME_TO_EXTENSION: Record<string, string> = {
  'application/pdf': 'pdf',
  'application/msword': 'doc',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx'
}

interface UploadState {
  status: 'idle' | 'generating-url' | 'uploading' | 'success' | 'error'
  progress: number
  fileName: string | null
  error: string | null
}

/**
 * CvUploadForm - Step 2 of the job application process
 */
export const CvUploadForm: React.FC = () => {
  const { cvFileKey, cvFileName, setCvInfo, nextStep, prevStep } = useApplicationFormStore()
  const { t } = useTranslation()
  const [isDragging, setIsDragging] = useState(false)
  const [uploadState, setUploadState] = useState<UploadState>({
    status: cvFileKey ? 'success' : 'idle',
    progress: cvFileKey ? 100 : 0,
    fileName: cvFileName || null,
    error: null
  })
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { mutateAsync: generateUploadUrl } = useGenerateCvUploadUrl()

  const validateFile = (file: File): { valid: boolean; error?: string } => {
    const fileExtension = `.${file.name.split('.').pop()?.toLowerCase()}`
    if (!ACCEPTED_FILE_TYPES.includes(fileExtension)) {
      return { valid: false, error: t('application.upload.error.invalidType') }
    }
    if (!ACCEPTED_MIME_TYPES.includes(file.type)) {
      return { valid: false, error: t('application.upload.error.invalidFormat') }
    }
    if (file.size > MAX_FILE_SIZE) {
      return { valid: false, error: t('application.upload.error.tooLarge') }
    }
    return { valid: true }
  }

  const uploadToS3 = useCallback(async (file: File, presignedUrl: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest()

      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const percentComplete = Math.round((event.loaded / event.total) * 100)
          setUploadState((prev) => ({ ...prev, progress: percentComplete }))
        }
      })

      xhr.addEventListener('load', () => {
        if (xhr.status === 200) resolve()
        else reject(new Error(`S3 upload failed with status: ${xhr.status}`))
      })

      xhr.addEventListener('error', () => reject(new Error('Network error during S3 upload')))
      xhr.addEventListener('abort', () => reject(new Error('Upload aborted')))

      xhr.open('PUT', presignedUrl)
      xhr.setRequestHeader('Content-Type', file.type)
      xhr.send(file)
    })
  }, [])

  const handleFileSelect = useCallback(async (file: File) => {
    const validation = validateFile(file)
    if (!validation.valid) {
      setUploadState({ status: 'error', progress: 0, fileName: file.name, error: validation.error! })
      return
    }

    try {
      setUploadState({ status: 'generating-url', progress: 0, fileName: file.name, error: null })

      const extension = MIME_TO_EXTENSION[file.type] || file.name.split('.').pop() || ''
      const presignedResponse = await generateUploadUrl({
        mimeType: file.type,
        extension,
        originalFilename: file.name,
        fileSize: file.size
      })

      setUploadState({ status: 'uploading', progress: 0, fileName: file.name, error: null })
      await uploadToS3(file, presignedResponse.signedUrl)

      setCvInfo({
        cvFileKey: presignedResponse.key,
        cvFileName: file.name,
        cvMimeType: file.type
      })

      setUploadState({ status: 'success', progress: 100, fileName: file.name, error: null })
    } catch (error) {
      let errorMessage = t('application.upload.error.failed')

      if (error instanceof Error) {
        if (error.message.includes('Network error')) {
          errorMessage = t('application.upload.error.network')
        } else if (error.message.includes('timeout')) {
          errorMessage = t('application.upload.error.timeout')
        } else if (error.message.includes('expired')) {
          errorMessage = t('application.upload.error.expired')
        } else if (error.message.includes('S3')) {
          errorMessage = t('application.upload.error.server')
        }
      }

      setUploadState({ status: 'error', progress: 0, fileName: file.name, error: errorMessage })
    }
  }, [generateUploadUrl, uploadToS3, setCvInfo, t])

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) handleFileSelect(file)
  }

  const handleDropZoneClick = () => fileInputRef.current?.click()

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setIsDragging(false)
  }

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setIsDragging(false)
    const file = event.dataTransfer.files[0]
    if (file) handleFileSelect(file)
  }

  const handleRetry = () => {
    setUploadState({ status: 'idle', progress: 0, fileName: null, error: null })
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleNext = () => {
    if (uploadState.status === 'success') nextStep()
  }

  return (
    <div className="space-y-6">
      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf,.pdf,.doc,.docx,image/*"
        capture="environment"
        onChange={handleFileInputChange}
        className="hidden"
        aria-label="File upload input"
      />

      {/* Drop zone (idle state) */}
      {uploadState.status === 'idle' && (
        <div
          onClick={handleDropZoneClick}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-colors ${
            isDragging
              ? 'border-primary bg-primary/5'
              : 'border-card-border hover:border-card-text-secondary hover:bg-gray-50'
          }`}
          role="button"
          tabIndex={0}
          aria-label={t('application.upload.dropzoneAriaLabel')}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              handleDropZoneClick()
            }
          }}
        >
          <Upload className="w-12 h-12 text-card-text-secondary mx-auto mb-4" aria-hidden="true" />
          <p className="text-lg font-medium text-card-text mb-2">
            {t('application.upload.dragDrop')}
          </p>
          <p className="text-sm text-card-text-secondary">
            {t('application.upload.fileTypes')}
          </p>
        </div>
      )}

      {/* Generating URL state */}
      {uploadState.status === 'generating-url' && (
        <div className="border-2 border-primary/50 rounded-lg p-8 bg-primary/5">
          <div className="flex items-center gap-3">
            <Loader2 className="w-6 h-6 text-primary animate-spin" aria-hidden="true" />
            <div className="flex-1">
              <p className="text-sm font-medium text-card-text">{uploadState.fileName}</p>
              <p className="text-xs text-card-text-secondary">{t('application.upload.preparing')}</p>
            </div>
          </div>
        </div>
      )}

      {/* Uploading state with progress bar */}
      {uploadState.status === 'uploading' && (
        <div className="border-2 border-primary/50 rounded-lg p-8 bg-primary/5">
          <div className="flex items-center gap-3 mb-4">
            <Loader2 className="w-6 h-6 text-primary animate-spin" aria-hidden="true" />
            <div className="flex-1">
              <p className="text-sm font-medium text-card-text">{uploadState.fileName}</p>
              <p className="text-xs text-card-text-secondary">{t('application.upload.uploading')}</p>
            </div>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
            <div
              className="bg-primary h-full transition-all duration-300"
              style={{ width: `${uploadState.progress}%` }}
              role="progressbar"
              aria-valuenow={uploadState.progress}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={t('application.upload.progressAriaLabel', { percent: uploadState.progress })}
            />
          </div>
          <p className="text-sm text-card-text-secondary mt-2 text-center">{uploadState.progress}%</p>
        </div>
      )}

      {/* Success state */}
      {uploadState.status === 'success' && (
        <div className="border-2 border-primary rounded-lg p-8 bg-primary/5">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0" aria-hidden="true" />
            <div className="flex-1">
              <p className="text-sm font-medium text-card-text flex items-center gap-2">
                <FileText className="w-4 h-4" aria-hidden="true" />
                {uploadState.fileName}
              </p>
              <p className="text-xs text-primary mt-1">{t('application.upload.success')}</p>
            </div>
          </div>
        </div>
      )}

      {/* Error state */}
      {uploadState.status === 'error' && (
        <div>
          <div className="border-2 border-error rounded-lg p-8 bg-red-50">
            <div className="flex items-center gap-3 mb-4">
              <AlertCircle className="w-6 h-6 text-error flex-shrink-0" aria-hidden="true" />
              <div className="flex-1">
                <p className="text-sm font-medium text-card-text">{uploadState.fileName}</p>
                <p className="text-sm text-error mt-1" role="alert">
                  {uploadState.error}
                </p>
              </div>
            </div>
            <button
              onClick={handleRetry}
              className="flex items-center gap-2 px-4 py-2 bg-error text-white rounded-md hover:opacity-90 transition-colors focus:outline-none focus:ring-2 focus:ring-error focus:ring-offset-2"
              type="button"
            >
              <RefreshCw className="w-4 h-4" aria-hidden="true" />
              {t('application.upload.retryButton')}
            </button>
          </div>
        </div>
      )}

      {/* Navigation buttons */}
      <div className="flex justify-between pt-4">
        <button
          type="button"
          onClick={prevStep}
          className="px-6 py-3 min-h-[44px] border border-card-border rounded-md font-medium text-card-text-secondary hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
        >
          {t('common.actions.back')}
        </button>
        <button
          type="button"
          onClick={handleNext}
          disabled={uploadState.status !== 'success'}
          className={`px-6 py-3 min-h-[44px] rounded-md font-medium transition-colors ${
            uploadState.status === 'success'
              ? 'bg-primary text-surface hover:bg-primary-hover focus:ring-2 focus:ring-primary focus:ring-offset-2'
              : 'bg-gray-200 text-card-text-secondary cursor-not-allowed'
          }`}
          aria-disabled={uploadState.status !== 'success'}
        >
          {t('common.actions.next')}
        </button>
      </div>
    </div>
  )
}
